<template>
  <div
    class="tw-mx-auto tw-w-full tw-text-center tw-font-semibold tw-text-xl tw-py-8"
    v-html="t(slaConfig.headline)"></div>
  <div class="tw-w-full tw-bg-primary-50 tw-h-full tw-flex tw-flex-col sm:tw-flex-row tw-items-stretch tw-justify-center tw-p-4 sm:tw-p-16">
    <div
      class="tw-w-full tw-bg-primary-500 tw-text-primary-contrast tw-flex tw-flex-col tw-items-start tw-justify-evenly tw-p-8 md:tw-p-16 tw-space-y-4 tw-space-x-4">
      <p class="tw-text-base tw-leading-relaxed tw-text-primary-contrast">
        {{ t(slaConfig.mainContent) }}
      </p>
      <div class="tw-flex tw-flex-row tw-items-center tw-justify-evenly">
        <BaseInput
          class="tw-w-48 tw-text-center"
          inputType="checkbox"
          label-alignment="horizontal"
          input-id="sla-consent"
          v-model="consent"></BaseInput>

        <label
          for="consent"
          class="tw-text-base tw-leading-8 tw-text-primary-contrast">
          <span
            data-test="signup-sla-consent-text"
            v-html="t(slaConfig.disclaimer)"></span>
          <a
            target="_blank"
            data-test="signup-sla-link"
            :href="slaUrl || '#'"
            @click="slaUrl ? null : $event.preventDefault()"
            v-text="t('signup.terms_and_conditions')"></a>
        </label>
      </div>
      <div
        class="tw-flex tw-justify-end tw-w-full"
        role="button"
        @click.prevent="handleAccept">
        <BaseButton
          class="tw-mt-4"
          variant="neutral"
          :text="t('platform.button_accept')"
          event-type="button"
          :disabled="!consent"
          :is-waiting="isPending"></BaseButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import BaseButton from "../../BaseButton/BaseButton.vue";
import BaseInput from "../../BaseInput/BaseInput.vue";

import { useTranslation } from "i18next-vue";
import { updateSLAStatus } from "@/utils/localStorage";
import axios, { AxiosRequestConfig } from "axios";
import { useGlobalConfigsStore } from "@/storeModules/globalConfigs/globalConfigs";
import { useUserConfigStore } from "@/storeModules/userConfigs";
import { useUISettingsStore } from "@/storeModules/uisettings/UISettingsStore";

const globalStore = useGlobalConfigsStore();
const uiStore = useUISettingsStore();
const userStore = useUserConfigStore();
const consent = ref<boolean>(false);
const { connector } = useGlobalConfigsStore();
const isPending = ref<boolean>(false);

const props = defineProps({
  isRcSLA: {
    type: Boolean,
    default: false,
  },
});
const emits = defineEmits<{
  accept: [];
}>();
const { t } = useTranslation();

const slaConfig = computed(() => {
  return props.isRcSLA
    ? {
        headline: "platform.headline_sla",
        disclaimer: "rc.sla_consent_disclaimer",
        mainContent: `wl.${globalStore.brand}.rc_sla_consent`,
        successMessage: "remote_control.sla.success",
        errorMessage: "remote_control.sla.error",
        slaType: "remote_control",
      }
    : {
        headline: "platform.headline_general_sla",
        disclaimer: "platform.general.sla_consent_disclaimer",
        mainContent: `wl.${globalStore.brand}.general_sla_consent`,
        successMessage: "platform.general.sla.success",
        errorMessage: "platform.general.sla.error",
        slaType: "general",
      };
});

const acceptSLARequest = async (): Promise<boolean> => {
  if (props.isRcSLA) {
    // Old API style
    const result = await globalStore.connector?.remoteControl.acceptRCSLA();

    if (result !== "OK") {
      uiStore.setError({
        message: t(slaConfig.value.errorMessage),
        type: "error",
      });
      return false;
    }

    userStore.userData.remoteControlSLA = true;
    updateSLAStatus("remoteControlSLA", true);
    return true;
  }
  // API v3 style
  const result = await globalStore.connector?.general.acceptUpdatedGeneralSLA();

  if (!result?.success) {
    uiStore.setError({
      message: t(result?.error?.messageKey ?? "errors.api_internal_error"),
      type: "error",
    });
    return false;
  }

  userStore.userData.acceptedLatestSla = true;
  updateSLAStatus("acceptedLatestSla", true);

  return true;
};

const handleAccept = async () => {
  if (!consent.value) return;
  isPending.value = true;

  try {
    const success = await acceptSLARequest();
    if (!success) return;

    emits("accept");

    uiStore.setError({
      message: t(slaConfig.value.successMessage),
      type: "success",
    });
  } catch (error) {
    console.error(error);
    uiStore.setError({
      message: t(error as string, { defaultValue: t(slaConfig.value.errorMessage) }),
      type: "error",
    });
  } finally {
    isPending.value = false;
  }
};

const slaUrl = ref<string | null>(null);

const loadSLA = async () => {
  const path = `/api/get-sla-by-user?session=${connector?.remoteControl.sessionID}&slaType=${slaConfig.value.slaType}`;

  const config = {
    url: path,
    method: "GET",
    responseType: "blob",
  };

  try {
    const response = await axios(config as AxiosRequestConfig);
    const blob = new Blob([response.data], {
      type: "application/pdf",
    });
    slaUrl.value = URL.createObjectURL(blob);
  } catch (error) {
    console.error(error);
    slaUrl.value = null;
  }
};

onMounted(() => {
  loadSLA();
});
</script>
